import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Clock, CheckCircle2, AlertCircle, Copy, MessageSquare } from 'lucide-react';
import { api } from '../lib/api';
import DoubtChatView from '../components/DoubtChatView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public-facing live-chat waiting room + chat surface.
 * URL: /complaint/chat/:chatId?t=<jwt>
 *   - Waiting room shows a 30s countdown while the backend tries to assign an agent.
 *   - On assignment → DoubtChatView (text + image only).
 *   - On timeout/fallback → shows ticket number + "we'll call you back" message.
 *   - On agent close → shows ticket number as a closed transcript.
 */
export default function PublicComplaintChat() {
  const { chatId } = useParams();
  const [search] = useSearchParams();
  const tokenFromUrl = search.get('t');
  const [token] = useState(() =>
    tokenFromUrl || (() => { try { return localStorage.getItem(`complaint_chat_${chatId}`) || ''; } catch { return ''; } })()
  );
  const [loading, setLoading] = useState(true);
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  const pollRef = useRef(null);
  const [secondsWaited, setSecondsWaited] = useState(0);

  // Persist token
  useEffect(() => {
    if (token && chatId) {
      try { localStorage.setItem(`complaint_chat_${chatId}`, token); } catch (e) { /* noop */ }
    }
  }, [token, chatId]);

  const fetchAll = async () => {
    try {
      const [s, m] = await Promise.all([
        api.getComplaintChatStatus(chatId, token),
        api.getPublicComplaintChatMessages(chatId, token),
      ]);
      setChat(s.data);
      setMessages(m.data.messages || []);
      return s.data;
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unable to load chat');
      return null;
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!token) { setLoading(false); setError('Missing session token — please re-open the link.'); return; }
    fetchAll();
    // Poll while waiting; when active/closed, WS drives updates but we keep a slow poll as a fallback
    pollRef.current = setInterval(async () => {
      const s = await fetchAll();
      // stop fast-polling once we have a resolution
      if (s && s.status !== 'waiting') {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(fetchAll, 8000); // slow fallback
      }
    }, 2000);
    return () => { clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, token]);

  // Countdown timer for waiting state
  useEffect(() => {
    if (!chat?.created_at || chat.status !== 'waiting') return;
    const created = new Date(chat.created_at).getTime();
    const tick = () => setSecondsWaited(Math.floor((Date.now() - created) / 1000));
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [chat?.created_at, chat?.status]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300"><Loader2 className="animate-spin" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300 p-4">
        <div className="max-w-md text-center bg-slate-900/70 rounded-2xl p-6 border border-slate-700">
          <AlertCircle className="mx-auto text-rose-400 mb-3" size={40} />
          <h1 className="text-lg font-semibold text-white">{error}</h1>
          <a href="/complaint" className="text-cyan-300 text-sm underline mt-3 inline-block">Go back</a>
        </div>
      </div>
    );
  }
  if (!chat) return null;

  const wsUrl = () => {
    if (!chatId || !token) return null;
    const base = BACKEND_URL.replace(/^http/, 'ws');
    return `${base}/api/complaint-chat/ws/${chatId}?token=${encodeURIComponent(token)}&role=student`;
  };

  // ===== Waiting =====
  if (chat.status === 'waiting') {
    const remaining = Math.max(0, 30 - secondsWaited);
    const pct = Math.min(100, (secondsWaited / 30) * 100);
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-950 via-slate-950 to-indigo-950 text-slate-100 flex items-center justify-center p-4" data-testid="complaint-chat-waiting">
        <div className="w-full max-w-md bg-slate-900/80 border border-slate-700 rounded-2xl p-6 backdrop-blur">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-cyan-500/20 border border-cyan-400/40 mx-auto mb-4">
            <Clock className="text-cyan-300" size={30} />
          </div>
          <h1 className="text-xl font-bold text-center">Connecting you to an agent…</h1>
          <p className="text-sm text-slate-400 text-center mt-1">This usually takes just a few seconds.</p>

          {/* Progress bar */}
          <div className="mt-6 h-2 w-full bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all" style={{ width: `${pct}%` }} data-testid="complaint-chat-progress" />
          </div>
          <div className="text-center text-xs text-slate-400 mt-2">{remaining}s remaining before we register your ticket</div>

          <div className="mt-6 bg-slate-800/60 rounded-lg p-3 text-xs text-slate-300 border border-slate-700">
            <div className="font-semibold mb-1 text-slate-200">Your message:</div>
            <p className="whitespace-pre-wrap break-words">{chat.first_message}</p>
          </div>
        </div>
      </div>
    );
  }

  // ===== Fallback (timeout, no agent) =====
  if (chat.status === 'fallback') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-950 via-slate-950 to-slate-900 text-slate-100 flex items-center justify-center p-4" data-testid="complaint-chat-fallback">
        <div className="w-full max-w-md bg-slate-900/80 border border-slate-700 rounded-2xl p-6 backdrop-blur text-center">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20 border border-amber-400/40 mx-auto mb-4">
            <MessageSquare className="text-amber-300" size={30} />
          </div>
          <h1 className="text-xl font-bold">We couldn't connect you right now</h1>
          <p className="text-sm text-slate-300 mt-2">Your complaint has been registered as a ticket. Our team will contact you shortly.</p>
          {chat.legacy_ticket_number && (
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Ticket number</div>
              <div className="inline-flex items-center gap-2 bg-slate-800 border border-slate-700 px-3 py-2 rounded-lg">
                <code className="font-mono font-bold text-amber-300">{chat.legacy_ticket_number}</code>
                <button onClick={() => { navigator.clipboard.writeText(chat.legacy_ticket_number); toast.success('Copied'); }}
                        className="text-slate-400 hover:text-white">
                  <Copy size={14} />
                </button>
              </div>
            </div>
          )}
          <a href={`/complaint?ticket=${chat.legacy_ticket_number || ''}`}
             className="mt-6 inline-block text-sm text-cyan-300 underline">Track this ticket →</a>
        </div>
      </div>
    );
  }

  // ===== Closed (agent closed the chat) =====
  const closedReadonly = chat.status === 'closed';

  // ===== Active or Closed → chat surface =====
  return (
    <div className="h-[100dvh] bg-slate-950 flex flex-col overflow-hidden" data-testid="complaint-chat-active">
      <div className="px-4 py-2 bg-slate-900/90 border-b border-slate-700 flex items-center gap-3 shrink-0">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold border ${closedReadonly ? 'bg-slate-500/20 border-slate-500 text-slate-300' : 'bg-emerald-500/20 border-emerald-400/40 text-emerald-300'}`}>
          {(chat.agent_name || 'A')[0]?.toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-100 truncate">{chat.agent_name || 'Support Agent'}</div>
          <div className="text-[11px] text-slate-400 truncate">
            {closedReadonly ? (
              <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 size={12} /> Resolved · Ticket {chat.legacy_ticket_number}</span>
            ) : (
              chat.problem_type || 'Support'
            )}
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <DoubtChatView
          chatId={chat.id}
          wsUrl={wsUrl()}
          identity="complainant"
          initialMessages={messages}
          hideVoice={false}
          disabled={closedReadonly}
          header={null}
          onSendText={async (content) => (await api.sendPublicComplaintChatText(chat.id, token, content)).data}
          onSendMedia={async (file, mediaType) => (await api.sendPublicComplaintChatMedia(chat.id, token, file, mediaType)).data}
        />
      </div>
      {closedReadonly && chat.solution_remarks && (
        <div className="p-3 bg-emerald-900/40 border-t border-emerald-700 text-xs text-emerald-100">
          <div className="font-semibold mb-1">Resolution</div>
          {chat.solution_remarks}
        </div>
      )}
    </div>
  );
}
